import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  FilterCategoryItemProps,
  StaticAutocompleteFilterConfig,
  TableFilterOption,
} from '@/components/data-table/filters-v2';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import { SelectedOptionsSection, shouldShowSelectedSection } from './selected-options-section';

/**
 * Submenu content for autocomplete filter
 */
export function AutocompleteFilterSubmenu({
  config,
  activeValues,
  onFilterChange,
  onClearFilter,
  onCloseSubmenu,
}: FilterCategoryItemProps & {
  config: StaticAutocompleteFilterConfig;
  onCloseSubmenu?: () => void;
}) {
  const { t } = useTranslation('common');
  const selected = new Set(activeValues);
  const listRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');

  // Controlled filtering (shouldFilter=false) so the selected section + its
  // dividers can be hidden during an active search — cmdk's built-in filter can't
  // hide the plain divider elements, which would otherwise dangle.
  const normalizedSearch = searchValue.trim().toLowerCase();
  const visibleOptions = normalizedSearch
    ? config.options.filter(opt => opt.label.toLowerCase().includes(normalizedSearch))
    : config.options;
  // Only pin a selected section once the list is long enough to be hard to scan.
  const showSelectedSection =
    !normalizedSearch &&
    selected.size > 0 &&
    shouldShowSelectedSection(!!config.withSearch, config.options.length);

  const toggle = (value: string) => {
    const newSelected = new Set(selected);
    if (selected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    onFilterChange(config.name, Array.from(newSelected));
  };

  const handleClearAll = () => {
    onClearFilter(config.name);
    onCloseSubmenu?.();
  };

  // Jump the option list back to the top selected section (footer count click).
  const scrollListToTop = () => {
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderLabel = (opt: TableFilterOption) => {
    if (config.renderLabel) {
      return config.renderLabel(opt.label, opt);
    }

    return (
      <div
        className={cn('flex items-center justify-start gap-2', opt.color && `text-${opt.color}`)}
      >
        {config.displayDot && (
          <div className="flex h-4 w-4 min-w-4 items-center justify-center">
            <span
              className="block h-1.5 min-h-1.5 w-1.5 min-w-1.5 rounded-full"
              style={{ backgroundColor: 'currentColor' }}
            />
          </div>
        )}
        <span className="text-sm">{opt.label}</span>
      </div>
    );
  };

  return (
    <Command shouldFilter={false}>
      {config.withSearch && (
        <CommandInput
          placeholder={t('filters.search', { value: config.label })}
          className="h-9"
          value={searchValue}
          onValueChange={setSearchValue}
        />
      )}
      <CommandList ref={listRef} className="max-h-[200px] p-1">
        {visibleOptions.length === 0 ? (
          <CommandEmpty>{t('noResultsFound')}</CommandEmpty>
        ) : (
          <CommandGroup>
            {/* Selected options are pinned to their own section on top, followed by
                a divider. The full list below still shows them (selected +
                unselected); both share `toggle`, so a change in either stays in
                sync. */}
            {showSelectedSection && (
              <SelectedOptionsSection
                filterName={config.name}
                selectedValues={activeValues}
                loadedOptions={config.options}
                onToggle={toggle}
                renderLabel={renderLabel}
              />
            )}
            {visibleOptions.map(opt => {
              const isSelected = selected.has(opt.value);
              return (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => toggle(opt.value)}
                  className={cn('flex cursor-pointer items-center gap-2', isSelected && 'bg-muted')}
                >
                  <Checkbox checked={isSelected} />
                  <Label className="cursor-pointer">{renderLabel(opt)}</Label>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
      <CommandSeparator />
      <CommandGroup className="p-2">
        <div className="flex items-center justify-between">
          <Button size="sm" className="h-7" onClick={handleClearAll} disabled={selected.size === 0}>
            {t('filters.clearAll')}
          </Button>
          {/* Clicking the count scrolls the list back up to the selected section. */}
          <button
            type="button"
            onClick={scrollListToTop}
            disabled={selected.size === 0}
            className="rounded-sm text-xs text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none"
          >
            {t('filters.selected', { count: selected.size })}
          </button>
        </div>
      </CommandGroup>
    </Command>
  );
}
